(() => {
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-fiaco-product-gallery]').forEach((root) => {
      if (root.fiacoGallery) return;
      const native = root.tagName === 'MEDIA-GALLERY';
      const track = root.querySelector(native ? '[id^="Slider-Gallery-"]' : '[data-gallery-track]');
      if (!track) return;
      const thumbs = root.querySelector(native ? '[id^="Slider-Thumbnails-"]' : '[data-gallery-thumbnails]');
      const controls = root.querySelector(native ? '[id^="GalleryViewer-"] > .slider-buttons' : '[data-gallery-controls]');
      const previous = controls?.querySelector(native ? '[name="previous"]' : '[data-gallery-previous]');
      const next = controls?.querySelector(native ? '[name="next"]' : '[data-gallery-next]');
      const current = controls?.querySelector(native ? '.slider-counter--current' : '[data-gallery-current]');
      const total = controls?.querySelector(native ? '.slider-counter--total' : '[data-gallery-total]');
      let index = 0;
      let frame = 0;
      let drag;
      let suppressClick = false;
      const items = () => Array.from(track.children).filter((item) => item.hasAttribute('data-media-id'));
      const thumbButtons = () => Array.from(thumbs?.querySelectorAll('button') || []);
      const thumbId = (button) => button.dataset.mediaTarget || button.closest('[data-target]')?.dataset.target;
      const behavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const update = (position) => {
        const slides = items();
        index = Math.max(0, Math.min(position, slides.length - 1));
        const active = slides[index];
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === active));
        if (current) current.textContent = String(index + 1);
        if (total) total.textContent = String(slides.length);
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
        if (controls) controls.hidden = slides.length < 2;
        root.dataset.galleryIndex = String(index);
        thumbButtons().forEach((button) => {
          const selected = thumbId(button) === active?.dataset.mediaId;
          button.classList.toggle('is-active', selected);
          if (selected) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
          if (selected && thumbs) {
            const box = button.getBoundingClientRect();
            const viewport = thumbs.getBoundingClientRect();
            if (box.left < viewport.left || box.right > viewport.right) {
              thumbs.scrollTo({ left: thumbs.scrollLeft + box.left - viewport.left - (thumbs.clientWidth - box.width) / 2, behavior: behavior() });
            }
          }
        });
      };
      const select = (position, smooth = true) => {
        const slides = items();
        const target = slides[Math.max(0, Math.min(position, slides.length - 1))];
        if (!target) return;
        const offset = target.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
        track.scrollTo({ left: offset, behavior: smooth ? behavior() : 'instant' });
        update(slides.indexOf(target));
        if (native && typeof root.playActiveMedia === 'function') root.playActiveMedia(target);
      };
      root.fiacoGallery = {
        selectMedia(mediaId) {
          const position = items().findIndex((slide) => slide.dataset.mediaId === String(mediaId));
          if (position >= 0) select(position);
        }
      };
      root.addEventListener('click', (event) => {
        if (suppressClick && track.contains(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const button = event.target.closest('button');
        if (!button) return;
        if (button === previous || button === next) {
          event.preventDefault();
          event.stopImmediatePropagation();
          select(index + (button === next ? 1 : -1));
        } else if (thumbs?.contains(button)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          root.fiacoGallery.selectMedia(thumbId(button));
        }
      }, true);
      track.tabIndex = 0;
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        select(event.key === 'Home' ? 0 : event.key === 'End' ? items().length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const left = track.getBoundingClientRect().left;
          let nearest = 0;
          let distance = Infinity;
          items().forEach((slide, position) => {
            const delta = Math.abs(slide.getBoundingClientRect().left - left);
            if (delta < distance) { distance = delta; nearest = position; }
          });
          update(nearest);
        });
      }, { passive: true });
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, input, select, textarea, video, model-viewer')) return;
        drag = { pointer: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft, active: false };
      });
      track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.pointer) return;
        const dx = event.clientX - drag.x;
        if (!drag.active && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(event.clientY - drag.y)) {
          drag.active = true;
          root.classList.add('fiaco-gallery-dragging');
          track.setPointerCapture(event.pointerId);
        }
        if (drag.active) { event.preventDefault(); track.scrollLeft = drag.left - dx; }
      });
      const endDrag = (event) => {
        if (!drag || event.pointerId !== drag.pointer) return;
        const moved = drag.active;
        drag = undefined;
        root.classList.remove('fiaco-gallery-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        if (moved) {
          suppressClick = true;
          select(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
          window.setTimeout(() => { suppressClick = false; }, 150);
        }
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      new ResizeObserver(() => select(index, false)).observe(track);
      new MutationObserver(() => {
        const active = items().findIndex((slide) => slide.classList.contains('is-active'));
        select(active < 0 ? index : active, false);
      }).observe(track, { childList: true });
      const initial = items().findIndex((slide) => slide.dataset.mediaId === root.dataset.initialMediaId);
      select(initial < 0 ? 0 : initial, false);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
